import React from 'react';
import styled from 'styled-components/macro';
import {
  FustagerWithoutAnlaeg,
  Item, MomsChange, Money, PriceSum,
} from '../../../containers/commerce';
import { LiterFormat, VolumePrUnit } from '../../Format';
import { sanitizeMeasurement } from '../../../lib/helpers';
import { sanitizePrice } from '../../../lib/price';
import { Col, Row, Div } from '../../grid';
import {
  Paragraph, Bold, LinkStyled, Span, Heading4,
} from '../../typography';
import { Image } from '../../Image';
import PriceText from '../Item/list-views/PriceText';
import { AlcoholPercentage, FadKobling } from '../parts';
import KlapbarUdsolgt from '../../UdsolgtTexts/KlapbarUdsolgt';
import Fjernlager from '../../Fjernlager';
import PalleItem from '../Item/PalleItem';
import BuyButtonsFadoel from '../Item/BuyButtonsFadoel';
import TooltipModalLink from '../../TooltipModal/TooltipModalLink';
import AnlaegUdsolgt from '../../UdsolgtTexts/AnlaegUdsolgt';

const NameWrapper = styled.div`
  margin: 10px 0;
  text-transform: Capitalize;
  font-family: 'Inter', sans-serif;
  font-weight: bold;
  font-size: 16px;
  line-height: 1.5em;

  & > a {
    font-family: 'Inter', sans-serif;
    font-weight: bold;
    font-size: 16px;
  }
`;

const LinkName = styled(LinkStyled)`
  color: ${({ theme }) => theme.colors.colorGreyDark};
`;

const RenderName = ({
  name,
  url,
  countPrColli,
  liter,
  productTypeFadoel,
  showOnlyName,
  productTypePack,
  productTypeReservedel,
  imageSingle,
  imagePlural,
  customTitle,
}) => {
  const isLink = url && !customTitle;
  const isCustomTitle = url && customTitle;
  return (
    <NameWrapper>
      {isLink ? (
        <LinkName className={(!imageSingle || !imagePlural) && 'bucketAnimation'} to={url}>
          {name}
          {!productTypeFadoel && !showOnlyName && !productTypePack && !productTypeReservedel && (
            <React.Fragment>
              &nbsp;{countPrColli}x<VolumePrUnit liter={liter} amount={countPrColli} />
            </React.Fragment>
          )}
        </LinkName>
      ) : isCustomTitle ? (
        <LinkName className={(!imageSingle || !imagePlural) && 'bucketAnimation'} to={url}>
          {customTitle}
        </LinkName>
      ) : (
        <Paragraph className={(!imageSingle || !imagePlural) && 'bucketAnimation'}>
          {name}{!productTypeFadoel && !showOnlyName && !productTypePack && !productTypeReservedel && (<React.Fragment> {countPrColli}x<VolumePrUnit liter={liter} amount={countPrColli} /></React.Fragment>)}
        </Paragraph>
      )}
    </NameWrapper>
  );
};

const fadoelsTypeMap = {
  Pilsner: '/fadoel/pilsner',
  'Stærk Pilsner': '/fadoel/pilsner',
  Classic: '/fadoel/classic',
  'Mørk Pilsner': '/fadoel/classic',
  Hvedeøl: '/fadoel/hvedeoel',
  'India Pale Ale': '/fadoel/ipa',
  'Brown Ale': '/fadoel/ale/brown-ale',
  'Lys Ale': '/fadoel/ale/lys-ale',
  'Mørk Ale': '/fadoel/ale/moerk-ale',
  'Rød Ale': '/fadoel/ale/moerk-ale',
  Stout: '/fadoel/porter',
};

const Fadoelstype = ({
  fadoelsType,
}) => {
  const link = fadoelsTypeMap[fadoelsType];
  if (link) {
    return <LinkStyled to={link}>{fadoelsType}</LinkStyled>;
  }
  return null;
};

const DivWrap = styled(Div)`
  border: 1px solid ${({ theme }) => theme.colors.colorGreyLightAlt};
  border-radius: 15px;
`;

const RenderSingleImage = ({
  imageSingle,
  productTypePack,
  productTypeKulsyre,
  bucketAnimation,
  productPageXtraProductTrue,
}) => {
  if (!imageSingle) {
    return null;
  }

  const imageHeight = productTypePack || productTypeKulsyre || productPageXtraProductTrue ? '200px' : '100px';

  return (
    <Image
      maxHeight={imageHeight}
      imageHeight={imageHeight}
      maxWidth="100%"
      name={imageSingle}
      className={bucketAnimation && 'bucketAnimation'}
    />
  );
};

const RenderPluralImage = ({ imagePlural, productTypeFadoel }) => {
  if (!imagePlural) {
    return null;
  }
  return (
    <Image
      imageHeight="100px"
      name={imagePlural}
      className="bucketAnimation"
      maxWidth={productTypeFadoel ? '80px' : '100%'}
    />
  );
};
const ImageWrapper = styled(Div)`
  display: grid;
  grid-auto-flow: column;
  min-height: 130px;
  align-items: center;
  justify-items: center;
  width: calc(100% - 15px);
  position: relative;
  left: 50%;
  transform: translateX(-50%);
`;

const RenderImages = ({
  imageSingle,
  singleWidth,
  imagePlural,
  pluralWidth,
  productTypePack,
  productTypeKulsyre,
  productTypeFadoel,
  productPageXtraProductTrue,
}) => {
  if (!imageSingle && !imagePlural) {
    return null;
  }

  const singleImage = (
    <RenderSingleImage
      imageSingle={imageSingle}
      productTypePack={productTypePack}
      singleWidth={singleWidth}
      hasPluralImage={Boolean(imagePlural)}
      productTypeKulsyr={productTypeKulsyre}
      productPageXtraProductTrue={productPageXtraProductTrue}
      bucketAnimation
    />
  );

  const pluralImage = (
    <RenderPluralImage
      imagePlural={imagePlural}
      pluralWidth={pluralWidth}
      productTypeFadoel={productTypeFadoel}
    />
  );

  if (imageSingle && !imagePlural) {
    return (
      <ImageWrapper>
        {singleImage}
      </ImageWrapper>
    );
  }
  if (!imageSingle && imagePlural) {
    return (
      <ImageWrapper>
        {pluralImage}
      </ImageWrapper>
    );
  }
  return (
    <ImageWrapper>
      {singleImage}
      {pluralImage}
    </ImageWrapper>
  );
};

const ProductCategoryNoStyle = ({ categories, ...otherProps }) => {
  switch (true) {
    case categories.includes('sukkerfri'):
      return <Div {...otherProps}>Sukkerfri</Div>;
    case categories.includes('saft'):
      return <Div {...otherProps}>Saft</Div>;
    default:
      return null;
  }
};

const ProductCategory = styled(ProductCategoryNoStyle)`
  color: ${({ theme }) => theme.colors.colorWhite};
  display: inline-block;
  position: absolute;
  right: 4px;
  top: -1px;
  padding: 3px 5px;
  font-size: 12px;
  ${({ categories, theme }) => (categories.includes('sukkerfri') ? `background: ${theme.colors.colorSukkerfri};` : '')}
  ${({ categories, theme }) => (categories.includes('saft') ? `background: ${theme.colors.colorSaft};` : '')}
`;

const ColImages = ({
  categories,
  imageSingle,
  singleWidth,
  imagePlural,
  pluralWidth,
  modular,
  flex,
  productTypePack,
  imagePluralFirst,
  productTypePalle,
  productTypeFadoel,
  productTypeGlass,
  imageSingleFirst,
  productTypeKulsyre,
  productPageXtraProductTrue,
  url,
}) => (
  <Col xs={12} sm={productTypeKulsyre ? 2 : 12} textCenter padding={productTypeKulsyre ? '15px 5px' : '0'}>
    <ProductCategory categories={categories} />
    {url && (
      <LinkStyled to={url} noStyle>
        <RenderImages
          imageSingle={imageSingle}
          singleWidth={singleWidth}
          imagePlural={imagePlural}
          pluralWidth={pluralWidth}
          modular={modular}
          flex={flex}
          productTypePack={productTypePack}
          imagePluralFirst={imagePluralFirst}
          productTypePalle={productTypePalle}
          productTypeFadoel={productTypeFadoel}
          productTypeGlass={productTypeGlass}
          imageSingleFirst={imageSingleFirst}
          productTypeKulsyre={productTypeKulsyre}
          productPageXtraProductTrue={productPageXtraProductTrue}
        />
      </LinkStyled>
    )}
    {!url && (
      <RenderImages
        imageSingle={imageSingle}
        singleWidth={singleWidth}
        imagePlural={imagePlural}
        pluralWidth={pluralWidth}
        modular={modular}
        flex={flex}
        productTypePack={productTypePack}
        imagePluralFirst={imagePluralFirst}
        productTypePalle={productTypePalle}
        productTypeFadoel={productTypeFadoel}
        productTypeGlass={productTypeGlass}
        imageSingleFirst={imageSingleFirst}
        productTypeKulsyre={productTypeKulsyre}
        productPageXtraProductTrue={productPageXtraProductTrue}
      />
    )}
  </Col>
);

const RenderButtons = ({
  amount,
  actions,
  inStock,
  inStockText,
}) => (inStock === '-1' && !inStockText ? (
  <React.Fragment>
    <Paragraph red>
      Midlertidigt udsolgt fra producenten
    </Paragraph>
  </React.Fragment>
) : inStock === '-1' && inStockText ? (
  <React.Fragment>
    <Paragraph red>
      {inStockText()}
    </Paragraph>
  </React.Fragment>
) : inStock === '100' ? (
  <Paragraph red>
    Udgået fra producenten
  </Paragraph>
) : (
  <BuyButtonsFadoel fullWidth amount={amount} actions={actions} />
));

const RenderLiterStkPris = ({
  price,
  countPrColli,
  showOnlyPluralPrice,
}) => {
  const money = sanitizePrice(price) / countPrColli;
  if (showOnlyPluralPrice) {
    return null;
  }
  return (
    <React.Fragment>
      <Col xs={12} padding="15px 0 0 0">
        <Money money={money} /> / stk.
      </Col>
    </React.Fragment>
  );
};

const RenderDescription = ({
  description,
  customDescription,
}) => (
  <Div marginTop="10px">
    {customDescription || (description && description()) || null}
  </Div>
);

const RenderDescriptionXtra = ({ descriptionXtra }) => (descriptionXtra ? <Paragraph fontWeight="bold">{descriptionXtra}</Paragraph> : null);


const RenderPallePricePrPiece = ({ palleItem }) => (palleItem ? (
  <Item
    varenr={palleItem}
    display={PalleItem}
  />
) : null);

const RenderKolliPalle = ({ antalKolliPalle }) => (
  <Col xs={12} padding="0px" marginBottom="10px">
    <Span>({antalKolliPalle})</Span>
  </Col>
);

const RenderNumberOfGlasses = ({ countPrColli }) => (
  <React.Fragment>
    <Col xs={6} padding="0px" marginBottom="15px">
      <Bold>Antal:</Bold>
    </Col>
    <Col xs={6} padding="0px" marginBottom="15px" textRight>
      {countPrColli} stk.
    </Col>
  </React.Fragment>
);

const RenderSizeFadoel = ({ liter }) => (
  <React.Fragment>
    <Col xs={6} padding="0px">
      <Bold>Størrelse:</Bold>
    </Col>
    <Col xs={6} padding="0px" textRight>
      <LiterFormat liter={liter} noSuffix /> liter
    </Col>
  </React.Fragment>
);

const RenderAlcFadoel = ({ alcoholPercentage }) => (
  <React.Fragment>
    <Col xs={6} padding="5px 0 0 0">
      <Bold>Alkohol %:</Bold>
    </Col>
    <Col xs={6} padding="5px 0 0 0" textRight>
      <AlcoholPercentage value={alcoholPercentage} />
    </Col>
  </React.Fragment>
);

const RenderOelType = ({ oelType }) => (
  <React.Fragment>
    <Col xs={3} padding="5px 0 0 0">
      <Bold>Type:</Bold>
    </Col>
    <Col xs={9} textRight padding="5px 0 0 0">
      {oelType}
    </Col>
  </React.Fragment>
);

const RenderFadoelType = ({ fadoelsType }) => (
  <React.Fragment>
    <Col xs={3} padding="5px 0 0 0">
      <Bold>Type:</Bold>
    </Col>
    <Col xs={9} textRight padding="0">
      <Fadoelstype fadoelsType={fadoelsType} />
    </Col>
  </React.Fragment>
);

const RenderShowAlcPct = ({ alcoholPercentage }) => (
  <React.Fragment>
    <Col xs={7} padding="5px 0 0 0">
      <Bold>Alkohol %:</Bold>
    </Col>
    <Col xs={5} textRight padding="5px 0 0 0">
      <AlcoholPercentage value={alcoholPercentage} />
    </Col>
  </React.Fragment>
);

const RenderFadkobling = ({ fadKobling }) => (
  <React.Fragment>
    <Col xs={6} padding="5px 0 0 0">
      <Bold>Fadkobling:</Bold>
    </Col>
    <Col xs={6} padding="5px 0 0 0" textRight>
      <FadKobling value={fadKobling} />
    </Col>
  </React.Fragment>
);

const LiterPrisFadoel = ({
  price, liter,
}) => (
  <React.Fragment>
    <Col xs={12} padding="30px 0 0 0">
      <Money money={sanitizePrice(price) / sanitizeMeasurement(liter)} /> / ltr.
    </Col>
  </React.Fragment>
);

const PriceStyled = styled(Money)`
  font-size: 24px;
  font-weight: bold;
`;

const PallePriceStyled = styled(PriceSum)`
  font-size: 24px;
  font-weight: bold;
  padding-top: 3px;
  display: inline-block;
`;

const PallePriceStyled2 = styled(PriceSum)`
  font-size: 14px;
  margin-top: 15px;
  display: inline;
`;

const RenderPrice = ({
  price,
  productTypePalle,
  packageNoun,
  varenr,
  priceSuffix,
}) => (
  <React.Fragment>
    <Col xs={12} padding="5px 0 0 0">
      {productTypePalle ? (
        <React.Fragment>
          <Money money={price} /> / {packageNoun.toLowerCase()}<br />
          <PallePriceStyled itemPrice varenr={varenr} numberOfPaller={0} /><br />
          <MomsChange /><br />
          <Div marginTop="10px">
            <PallePriceStyled2 itemPrice moms pant varenr={varenr} numberOfPaller={1} /> <Span inline fontSize="14px">med EUR-palle, pant & moms</Span>
          </Div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <PriceStyled money={price} />{priceSuffix && (priceSuffix)}<br /><MomsChange />
        </React.Fragment>
      )}
    </Col>
  </React.Fragment>
);

const RenderPricePack = ({
  price, isLeje, replaceStkWithPackageNoun, simpleAmountDescriptor, extendedAmountDescriptor, packageNoun, countPrColli,
}) => (
  <React.Fragment>
    <Col xs={12} padding="10px 0 0 0">
      <Bold>
        <PriceText
          small
          price={price}
          isLeje={isLeje}
          replaceStkWithPackageNoun={replaceStkWithPackageNoun}
          simpleAmountDescriptor={simpleAmountDescriptor}
          extendedAmountDescriptor={extendedAmountDescriptor}
          packageNoun={packageNoun}
          countPrColli={countPrColli}
          momsIndication
        />
      </Bold>
    </Col>
  </React.Fragment>
);

const RenderPricetextsAndStock = ({
  inStock,
  dobbeltAnlaeg,
  betingerFustager,
  betingerFustagerUdstyr,
}) => (
  <React.Fragment>
    {betingerFustager && !dobbeltAnlaeg && (
      <Paragraph fontSize="12px" marginBottom="0" marginTop="5px">
        Lejeprisen betinger køb af fustager
      </Paragraph>
    )}
    {betingerFustager && dobbeltAnlaeg && (
      <Paragraph fontSize="12px" marginBottom="0" marginTop="5px">
        Lejeprisen betinger køb af fustager til begge anlæg
      </Paragraph>
    )}
    {betingerFustagerUdstyr && (
      <Paragraph fontSize="12px" marginBottom="0" marginTop="5px">
        Lejeprisen betinger køb af fustager, samt leje af udstyr
      </Paragraph>
    )}
    {inStock > 0 && inStock !== '100' && (
      <Col xs={12} padding="10px 0 0 0">
        <Span block fontSize="13px" red paddingTop="5px">
          {inStock} tilbage på lager!
        </Span>
      </Col>
    )}
  </React.Fragment>
);

const RenderUdsolgtAnlaeg = () => (
  <Div marginTop="10px">
    <AnlaegUdsolgt />
  </Div>
);

const RenderUdsolgtKlapbar = () => (
  <Div marginTop="10px">
    <KlapbarUdsolgt />
  </Div>
);

const RenderTooltip = ({ tooltip }) => (
  <Paragraph marginTop="10px">
    {tooltip && (
      <TooltipModalLink blue title="Flere oplysninger">
        {tooltip()}
      </TooltipModalLink>
    )}
  </Paragraph>
);

const RenderNotice2 = ({ notice2 }) => (
  <Div>
    {notice2()}
  </Div>
);

const OfferModal = styled(TooltipModalLink)`
  background: ${({ theme }) => theme.colors.colorYellow};
  color: ${({ theme }) => theme.colors.colorGreyDark};
  font-weight: bold;
  position: relative;
  bottom: 5px;
  max-width: 506px;
  line-height: 1.5em;
  padding: 5px 17px 5px 10px;
  border-radius: 10px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  font-size: 14px;

  &:hover {
    color: ${({ theme }) => theme.colors.colorWhite};
    background: ${({ theme }) => theme.colors.colorGreen};
  }
`;

const RenderOffer = ({ norrebro }) => (
  <React.Fragment>
    {norrebro && (
      <OfferModal title="Få 12 gratis glas ved køb af 2 fustager">
        <Row>
          <Col xs={12} md={4} textCenter>
            <Image name="produkter-fadoel.noerrebro-glas-tilbud.png" maxHeight="200px" />
          </Col>
          <Col xs={12} md={8}>
            <Heading4>Få 12 gratis glas ved køb af 2 fustager</Heading4>
            <Paragraph>
              Når du køber mindst 2 fustager fra Nørrebro Bryghus får du 12 gratis glas fra bryggeriet med din ordre.
            </Paragraph>
            <Paragraph>Max 1 omgang gratis glas pr. ordre.</Paragraph>
            <Paragraph>Kampagnen gælder i september og oktober 2023.</Paragraph>
          </Col>
        </Row>
      </OfferModal>
    )}
  </React.Fragment>
);

const ColWrap = ({
  children,
  productTypeKulsyre,
  productPageXtraProductTrue,
  triple,
  productTypePack,
}) => (
  <Col
    xs={12}
    sm={productTypeKulsyre ? 12 : productPageXtraProductTrue ? 12 : 6}
    md={productPageXtraProductTrue ? 12 : 6}
    lg={triple ? 4 : productTypePack ? 4 : productTypeKulsyre ? 6 : productPageXtraProductTrue ? 12 : 4}
    xl={triple ? 4 : productTypePack ? 4 : productTypeKulsyre ? 6 : productPageXtraProductTrue ? 12 : 3}
    xxl={triple ? 4 : productTypePack ? 2.4 : productTypeKulsyre ? 6 : productPageXtraProductTrue ? 12 : 2.4}
    padding={productPageXtraProductTrue ? '0px' : '0 7.5px'}
    marginBottom="15px"
    h100p={!!productPageXtraProductTrue}
  >
    {children}
  </Col>
);

export default React.forwardRef((props) => {
  const {
    actions,
    alcoholPercentage,
    amount,
    antalKolliPalle,
    betingerFustager,
    categories,
    countPrColli,
    customDescription,
    customTitle,
    description,
    descriptionXtra,
    dobbeltAnlaeg,
    extendedAmountDescriptor,
    extraItemRef,
    fadKobling,
    fadoelsType,
    flex,
    imagePlural,
    imagePluralFirst,
    imageSingle,
    imageSingleFirst,
    inStock,
    inStockText,
    isLeje,
    liter,
    modular,
    name,
    noDesriptionProductView,
    oelType,
    packageNoun,
    palleItem,
    pluralWidth,
    price,
    productPageXtraProductTrue,
    productTypeFadoel,
    productTypeGlass,
    productTypeKulsyre,
    productTypePack,
    productTypePalle,
    productTypeReservedel,
    replaceStkWithPackageNoun,
    showAlcPct,
    showOnlyName,
    showType,
    showTypeFadoel,
    simpleAmountDescriptor,
    singleWidth,
    betingerFustagerUdstyr,
    tooltip,
    triple,
    udsolgtAnlaeg,
    udsolgtKlapbar,
    url,
    varenr,
    notice2,
    showOnlyPluralPrice,
    priceSuffix,
    norrebro,
  } = props;
  return (
    <ColWrap triple={triple} productTypePack={productTypePack} productTypeKulsyre={productTypeKulsyre} productPageXtraProductTrue={productPageXtraProductTrue}>
      <DivWrap green={amount > 0} ref={extraItemRef} white={amount === 0} h100p>
        <Col xs={12} h100p padding="0 10px">
          <Row>
            {!productPageXtraProductTrue && (
              <ColImages categories={categories} flex={flex} imagePlural={imagePlural} imagePluralFirst={imagePluralFirst} imageSingle={imageSingle} imageSingleFirst={imageSingleFirst} modular={modular} pluralWidth={pluralWidth} productTypeFadoel={productTypeFadoel} productTypeGlass={productTypeGlass} productTypeKulsyre={productTypeKulsyre} productTypePack={productTypePack} productTypePalle={productTypePalle} singleWidth={singleWidth} productPageXtraProductTrue={productPageXtraProductTrue} url={url} />
            )}
            <Col xs={12} sm={productTypeKulsyre ? 10 : 12} padding={productTypeKulsyre ? '15px' : productPageXtraProductTrue ? '15px' : '5px 15px 10px 15px'}>
              <RenderName productPageXtraProductTrue={productPageXtraProductTrue} countPrColli={countPrColli} customTitle={customTitle} imagePlural={imagePlural} imageSingle={imageSingle} liter={liter} name={name} productTypeFadoel={productTypeFadoel} productTypePack={productTypePack} productTypeReservedel={productTypeReservedel} showOnlyName={showOnlyName} url={url} />
              {descriptionXtra && (<RenderDescriptionXtra descriptionXtra={descriptionXtra} />)}
              {antalKolliPalle && (<RenderKolliPalle antalKolliPalle={antalKolliPalle} />)}
              <Row margin="0 0 20px 0">
                {productTypeGlass && (<RenderNumberOfGlasses countPrColli={countPrColli} />)}
                {productTypeFadoel && !productTypePack && !productTypeReservedel && (<React.Fragment><RenderSizeFadoel liter={liter} /><RenderAlcFadoel alcoholPercentage={alcoholPercentage} /></React.Fragment>)}
                {showAlcPct && (<RenderShowAlcPct alcoholPercentage={alcoholPercentage} />)}
                {productTypeFadoel && fadKobling && !productTypePack && !productTypeReservedel && (<RenderFadkobling fadKobling={fadKobling} />)}
                {showType && (<RenderOelType oelType={oelType} />)}
                {showTypeFadoel && (<RenderFadoelType fadoelsType={fadoelsType} />)}
                {!productTypeFadoel && !productTypePack && !productTypeReservedel && !productTypeKulsyre && (<RenderLiterStkPris showOnlyPluralPrice={showOnlyPluralPrice} price={price} liter={liter} countPrColli={countPrColli} />)}
                {productTypeKulsyre && (<RenderLiterStkPris showOnlyPluralPrice={showOnlyPluralPrice} price={price} liter={liter} countPrColli={countPrColli} isLeje={isLeje} triple={triple} productTypePack={productTypePack} productTypeKulsyre={productTypeKulsyre} />)}
                {productTypeFadoel && (<LiterPrisFadoel price={price} liter={liter} triple={triple} productTypePack={productTypePack} productTypeKulsyre={productTypeKulsyre} />)}
                {!productTypePack && (<RenderPrice price={price} productTypePalle={productTypePalle} packageNoun={packageNoun} varenr={varenr} priceSuffix={priceSuffix} />)}
                {productTypePack && (<RenderPricePack price={price} isLeje={isLeje} replaceStkWithPackageNoun={replaceStkWithPackageNoun} simpleAmountDescriptor={simpleAmountDescriptor} extendedAmountDescriptor={extendedAmountDescriptor} packageNoun={packageNoun} countPrColli={countPrColli} />)}
                <RenderPricetextsAndStock betingerFustager={betingerFustager} dobbeltAnlaeg={dobbeltAnlaeg} inStock={inStock} productTypeKulsyre={productTypeKulsyre} productTypePack={productTypePack} productTypeReservedel={productTypeReservedel} betingerFustagerUdstyr={betingerFustagerUdstyr} triple={triple} />
              </Row>
              <RenderButtons amount={amount} actions={actions} inStock={inStock} inStockText={inStockText} productPageXtraProductTrue={productPageXtraProductTrue} />
              {(amount > 0) && (
                <FustagerWithoutAnlaeg>
                  {(fustagerWithoutAnlaeg) => fustagerWithoutAnlaeg && (
                    <Paragraph marginTop="15px">Husk <LinkStyled to="/fadoelsanlaeg">fadølsanlæg</LinkStyled></Paragraph>
                  )}
                </FustagerWithoutAnlaeg>
              )}
              <RenderOffer norrebro={norrebro} />
              {udsolgtAnlaeg && (<RenderUdsolgtAnlaeg />)}
              {udsolgtKlapbar && (<RenderUdsolgtKlapbar />)}
              {!productTypeGlass && !noDesriptionProductView && (<RenderDescription description={description} customDescription={customDescription} />)}
              {tooltip && (<RenderTooltip tooltip={tooltip} />)}
              <RenderPallePricePrPiece palleItem={palleItem} />
              {notice2 && (<RenderNotice2 notice2={notice2} />)}
              {inStock <= -2 && (
                <Paragraph blue paddingTop="5px" grey={inStock === '-1'}>
                  <Fjernlager inStock={inStock} />
                </Paragraph>
              )}
            </Col>
            {productPageXtraProductTrue && (
              <ColImages categories={categories} flex={flex} imagePlural={imagePlural} imagePluralFirst={imagePluralFirst} imageSingle={imageSingle} imageSingleFirst={imageSingleFirst} modular={modular} pluralWidth={pluralWidth} productTypeFadoel={productTypeFadoel} productTypeGlass={productTypeGlass} productTypeKulsyre={productTypeKulsyre} productTypePack={productTypePack} productTypePalle={productTypePalle} singleWidth={singleWidth} productPageXtraProductTrue={productPageXtraProductTrue} />
            )}
          </Row>
        </Col>
      </DivWrap>
    </ColWrap>
  );
});
